import { FormDocument } from '@/features/ui/DocumentsData/model/types';

import {
  AddressPayload,
  AttachmentPayload,
  buildOwner,
  generateApplicationAttachment,
  mapAddress,
  OwnerPayload,
  toStringValue,
} from '@/shared/lib/helpers/payloadHelpers';

import { ResponseInsuranceOrder } from '@/modules/bia/shared/model/types';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { DocumentTypeCode, FormDataType, UserRequestDraft, UserService } from '@/api/types';

import { collectAttachments } from './payloadHelpers';

type GeneratePayloadParams = {
  requestId: UserRequestDraft['id'];
  formData: FormDataType;
  service: UserService;
  serviceFileManager?: ServiceFileManager;
};

interface Payload {
  requestId: UserRequestDraft['id'];
  service: {
    id: string;
    name: string;
    code: string;
  };
  weapon: {
    id: Nullable<string>;
    type: Nullable<string>;
  };
  owner: OwnerPayload;
  is_addresses_match: boolean;
  registered_address: AddressPayload;
  declared_address: AddressPayload;
  org_npu: {
    id: Nullable<string>;
    name: Nullable<string>;
    isPayable?: boolean;
  };
  additional_documents: {
    additional_info?: Nullable<string>;
  };
  attachments: AttachmentPayload[];
  selected_insurances?: Nullable<string[]>;
}

export const generatePayload = async ({
  requestId,
  formData,
  service,
  serviceFileManager,
}: GeneratePayloadParams): Promise<{
  commonPayload: Payload;
  signedPayload: Payload & Partial<ReturnType<ServiceFileManager['getSignature']>>;
}> => {
  const passportDocument = (
    Array.isArray(formData.personPassportDocument) ? formData.personPassportDocument[0] : null
  ) as Nullable<FormDocument>;
  const hasActualAddress = (formData.isAddressesEqual as { id?: string })?.id === '0';
  const attachments = collectAttachments(formData, hasActualAddress);
  const applicationAttachment = await generateApplicationAttachment(
    DocumentTypeCode.COLD_STEEL_STORAGE_FOR_COLLECTION,
    serviceFileManager,
  );

  const commonPayload: Payload = {
    requestId,
    service: {
      id: service.id,
      name: service.name,
      code: service.code,
    },
    weapon: {
      id: toStringValue((formData.weapon as Record<string, unknown>)?.id),
      type: toStringValue((formData.weapon as Record<string, unknown>)?.weaponType),
    },
    owner: buildOwner(formData, passportDocument),
    is_addresses_match: !hasActualAddress,
    registered_address: mapAddress(formData.registeredAddress as Record<string, any>),
    declared_address: mapAddress(
      (hasActualAddress ? formData.actualAddress : formData.registeredAddress) as Record<string, any>,
    ),
    org_npu: {
      id: toStringValue((formData.orgNpu as Record<string, unknown>)?.id),
      name: toStringValue((formData.orgNpu as Record<string, unknown>)?.label),
    },
    additional_documents: {
      additional_info: toStringValue(formData.additionalInformation),
    },
    attachments,
  };

  if (formData.selectedInsurances) {
    commonPayload.selected_insurances = (formData.selectedInsurances as ResponseInsuranceOrder[]).map(
      (insurance: ResponseInsuranceOrder) => insurance.id,
    );
  }

  const signedPayload = {
    ...commonPayload,
    attachments: applicationAttachment ? [...attachments, applicationAttachment] : attachments,
    ...(serviceFileManager?.getSignature() ?? {}),
  };

  return {
    commonPayload,
    signedPayload,
  };
};
